import React, { forwardRef } from 'react';
import classNames from 'classnames';
import { FiChevronDown } from 'react-icons/fi';

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps extends Omit<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  'size'
> {
  label?: string;
  error?: string;
  helperText?: string;
  options: SelectOption[];
  placeholder?: string;
  isRequired?: boolean;
  selectSize?: 'sm' | 'md' | 'lg';
  containerClassName?: string;
  showPlaceholder?: boolean;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      error,
      helperText,
      options,
      placeholder = 'Select an option',
      isRequired,
      selectSize = 'md',
      showPlaceholder = true,
      containerClassName,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const sizeStyles = {
      sm: 'py-2 text-sm',
      md: 'py-3 text-base',
      lg: 'py-4 text-lg',
    };

    return (
      <div className={classNames('w-full', containerClassName)}>
        {label && (
          <label className="block mb-1.5 text-sm font-medium text-gray-800">
            {label}
            {isRequired && <span className="text-danger ml-1">*</span>}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            disabled={disabled}
            className={classNames(
              'w-full px-4 pr-10 rounded-lg border appearance-none transition-all duration-200',
              'focus:outline-none focus:ring-2 text-sm',
              sizeStyles[selectSize],
              {
                'border-gray-300 focus:border-purple-600 focus:ring-purple-100':
                  !error || error,
                'bg-gray-100 cursor-not-allowed text-gray-600': disabled,
                'bg-white': !disabled,
                'text-blue-mid-9': !props.value && !props.defaultValue,
                'text-gray-900': props.value || props.defaultValue,
              },
              className
            )}
            {...props}
          >
            {showPlaceholder && (
              <option value="" disabled className="text-gray-500">
                {placeholder}
              </option>
            )}
            {options.map(option => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className="text-gray-900"
              >
                {option.label}
              </option>
            ))}
          </select>
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-600">
            <FiChevronDown size={20} />
          </div>
        </div>
        {(error || helperText) && (
          <p
            className={classNames('mt-1.5 text-xs', {
              'text-danger': error,
              'text-gray-600': !error && helperText,
            })}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;
